import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiAward, FiStar, FiZap } from 'react-icons/fi';
import { useCompanyFeatures } from '../contexts/CompanyFeaturesContext';
import { useParams } from 'react-router-dom';

type BadgeType = 'basic' | 'vip' | 'platinum' | null;

const getCompanyBadge = (enabledFeatures: string[] | null = []): BadgeType => {
  if (enabledFeatures === null) return 'platinum';
  
  const basicFeatures = ['dashboard', 'clients', 'managers', 'tasks', 'calculator', 'calendar', 'settings', 'issues', 'about'];
  const vipFeatures = ['agents', 'slots', 'sms'];
  const allFeatures = [
    'dashboard', 'clients', 'managers', 'agents', 'leads', 'orders', 
    'inventory', 'slots', 'tasks', 'calendar', 'sticky-notes', 'calculator', 
    'chat', 'lumira-ai', 'sms', 'company-features', 'settings', 'issues', 'about', 'landing-page'
  ];

  if (enabledFeatures.length === 0) {
    return 'platinum';
  }

  const hasAllFeatures = allFeatures.every(feature => enabledFeatures.includes(feature));
  if (hasAllFeatures) {
    return 'platinum';
  }

  const hasBasic = basicFeatures.every(feature => enabledFeatures.includes(feature));
  const hasVip = vipFeatures.every(feature => enabledFeatures.includes(feature));
  if (hasBasic && hasVip) {
    return 'vip';
  }

  if (hasBasic) {
    return 'basic';
  }

  return null;
};

const getBadgeLevel = (badge: BadgeType): number => {
  if (badge === 'platinum') return 3;
  if (badge === 'vip') return 2;
  if (badge === 'basic') return 1;
  return 0;
};

const getBadgeLabel = (badge: BadgeType): string => {
  if (badge === 'platinum') return 'Platinum';
  if (badge === 'vip') return 'VIP';
  if (badge === 'basic') return 'Basic';
  return '';
};

const getBadgeImage = (badge: BadgeType): string => {
  if (badge === 'platinum') return '/platinum.png';
  if (badge === 'vip') return '/gold.png';
  if (badge === 'basic') return '/bronze.png';
  return '';
};

const getBadgeColor = (badge: BadgeType): string => {
  if (badge === 'platinum') return '#1a1a1a';
  if (badge === 'vip') return '#f59e0b';
  if (badge === 'basic') return '#b45309';
  return '#666';
};

const BadgeUpgradePopup: React.FC = () => {
  const { company } = useParams<{ company: string }>();
  const { enabledFeatures, loading } = useCompanyFeatures();
  const [showPopup, setShowPopup] = useState(false);
  const [newBadge, setNewBadge] = useState<BadgeType>(null);
  const [previousBadge, setPreviousBadge] = useState<BadgeType>(null);
  const lastBadgeRef = useRef<BadgeType>(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (loading || !company || company === 'superadmin') {
      lastBadgeRef.current = null;
      return;
    }

    const currentBadge = getCompanyBadge(enabledFeatures);
    
    // Initialize last badge if it doesn't exist
    if (lastBadgeRef.current === null && currentBadge) {
      lastBadgeRef.current = currentBadge;
      return;
    }
    
    // If we have a current badge and it's different from last badge, and it's an upgrade
    if (currentBadge && currentBadge !== lastBadgeRef.current && !isProcessingRef.current) {
      const currentLevel = getBadgeLevel(currentBadge);
      const lastLevel = getBadgeLevel(lastBadgeRef.current);
      
      // Only show if it's an upgrade (higher level)
      if (currentLevel > lastLevel) {
        isProcessingRef.current = true;
        setPreviousBadge(lastBadgeRef.current);
        setNewBadge(currentBadge);
        setShowPopup(true);
        
        // Update last badge reference
        lastBadgeRef.current = currentBadge;
      } else if (currentLevel < lastLevel) {
        // Badge downgraded, just update the reference
        lastBadgeRef.current = currentBadge;
      } else {
        // Same level but different badge (shouldn't happen, but handle it)
        lastBadgeRef.current = currentBadge;
      }
    } else if (currentBadge && currentBadge === lastBadgeRef.current) {
      // Badge hasn't changed, do nothing
    } else if (currentBadge) {
      // Update last badge if it changed but wasn't an upgrade
      lastBadgeRef.current = currentBadge;
    }
  }, [enabledFeatures, loading, company]);

  // Listen for company-features-updated event to detect badge changes
  useEffect(() => {
    const handleFeaturesUpdate = () => {
      // Reset processing flag when features are updated externally
      isProcessingRef.current = false;
    };

    window.addEventListener('company-features-updated', handleFeaturesUpdate);
    return () => {
      window.removeEventListener('company-features-updated', handleFeaturesUpdate);
    };
  }, []);

  const handleClose = () => {
    setShowPopup(false);
    // Reset processing flag when popup is closed so it can show again on next upgrade
    setTimeout(() => {
      isProcessingRef.current = false;
    }, 100);
  };

  if (!showPopup || !newBadge) return null;

  return (
    <AnimatePresence>
      {showPopup && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm"
            style={{ cursor: 'pointer' }}
          />

          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 50 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed inset-0 z-[10001] flex items-center justify-center p-4 pointer-events-none"
            dir="rtl"
          >
            <div 
              className="relative max-w-lg w-full rounded-3xl p-8 pointer-events-auto overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${getBadgeColor(newBadge)} 0%, ${getBadgeColor(newBadge)}dd 100%)`,
                boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 100px rgba(244, 124, 39, 0.3)',
                border: '2px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              {/* Animated background particles */}
              <div className="absolute inset-0 overflow-hidden">
                {[...Array(20)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 rounded-full bg-white/30"
                    initial={{
                      x: Math.random() * 100 + '%',
                      y: Math.random() * 100 + '%',
                      scale: 0,
                    }}
                    animate={{
                      scale: [0, 1, 0],
                      x: Math.random() * 100 + '%',
                      y: Math.random() * 100 + '%',
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: Math.random() * 2,
                    }}
                  />
                ))}
              </div>

              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors z-10"
              >
                <FiX className="text-white text-xl" />
              </button>

              <div className="relative z-10 text-center">
                {/* Badge Icon with Animation */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 200, 
                    damping: 15,
                    delay: 0.2 
                  }}
                  className="flex justify-center mb-6"
                >
                  <div className="relative">
                    <motion.div
                      animate={{
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, -5, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      className="w-32 h-32 rounded-full p-4"
                      style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                      }}
                    >
                      <img 
                        src={getBadgeImage(newBadge)} 
                        alt={getBadgeLabel(newBadge)}
                        className="w-full h-full object-contain"
                        style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.5))' }}
                      />
                    </motion.div>
                    
                    {/* Glowing ring */}
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      animate={{
                        boxShadow: [
                          '0 0 0px rgba(255,255,255,0.5)',
                          '0 0 40px rgba(255,255,255,0.8)',
                          '0 0 0px rgba(255,255,255,0.5)',
                        ],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                      }}
                    />
                  </div>
                </motion.div>

                {/* Title */}
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-4xl font-bold text-white mb-3"
                  style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}
                >
                  🎉 מזל טוב!
                </motion.h2>

                {/* Badge Name */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mb-4"
                >
                  <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/20 backdrop-blur-sm border border-white/30">
                    <FiAward className="text-white text-2xl" />
                    <span className="text-2xl font-bold text-white">
                      {getBadgeLabel(newBadge)} Level
                    </span>
                  </div>
                </motion.div>

                {/* Message */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="text-white/90 text-lg mb-6 leading-relaxed"
                >
                  {previousBadge ? (
                    <>
                      עליתם מרמה <span className="font-bold">{getBadgeLabel(previousBadge)}</span> לרמה <span className="font-bold">{getBadgeLabel(newBadge)}</span>!
                      <br />
                      כעת יש לכם גישה לכל התכונות המתקדמות.
                    </>
                  ) : (
                    <>
                      קיבלתם את תג <span className="font-bold">{getBadgeLabel(newBadge)}</span>!
                      <br />
                      כעת יש לכם גישה לכל התכונות המתקדמות.
                    </>
                  )}
                </motion.p>

                {/* Features List */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="flex items-center justify-center gap-4 flex-wrap mb-6"
                >
                  <div className="flex items-center gap-2 text-white/80">
                    <FiStar className="text-yellow-300" />
                    <span className="text-sm">תכונות מתקדמות</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/80">
                    <FiZap className="text-yellow-300" />
                    <span className="text-sm">ביצועים משופרים</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/80">
                    <FiAward className="text-yellow-300" />
                    <span className="text-sm">תמיכה ייעודית</span>
                  </div>
                </motion.div>

                {/* Continue Button */}
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  onClick={handleClose}
                  className="px-8 py-4 rounded-xl font-bold text-lg text-white transition-all duration-300 hover:scale-105"
                  style={{
                    background: 'rgba(255, 255, 255, 0.25)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                  }}
                >
                  המשך
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BadgeUpgradePopup;

